import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MatomoTracker from '@datapunt/matomo-tracker-js';
import { useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Button } from '../components/Button';
import { ClockIcon, OrangePlusIcon } from '../components/Icon';
import SvgIcon from '../components/SvgIcon';
import { BroadcastFrame, BroadcastFrameList, } from '../components/frames/BroadcastFrame';
import { LobbyFrame } from '../components/frames/LobbyFrame';
import { scheduleBroadcast } from '../components/popups/ScheduleBroadcastPopup';
import { settingsPopup } from '../components/popups/SettingsPopup';
import { useGoLiveNow } from '../hooks/broadcast';
import { useScheduledBroadcasts, } from '../state/scheduleState';
import { useThrottledTime } from '../state/timeState';
// Track when the script is loaded in production
// this emulates the behavior of embedding the tracking
// script in the HTML.
if (process.env.NODE_ENV === 'production') {
    const matomo = new MatomoTracker({
        urlBase: 'https://matomo.streamr.network/',
        siteId: 1,
    });
    matomo.trackPageView();
}
export function HomePage() {
    const broadcasts = useScheduledBroadcasts();
    const time = useThrottledTime(30000);
    const [pastBroadcasts, upcomingBroadcasts] = useMemo(() => {
        const past = [];
        const upcoming = [];
        for (const broadcast of broadcasts) {
            (broadcast.startsAt < time ? past : upcoming).push(broadcast);
        }
        return [past, upcoming];
    }, [broadcasts, time]);
    const startBroadcast = useGoLiveNow();
    const settingsButtonRef = useRef(null);
    return (_jsxs(HomePageRoot, { children: [_jsxs(Header, { children: [_jsx("div", { children: _jsx(LogoButton, { type: "button", kind: "secondary", size: "mini", shape: "pill", padding: "small", variant: "glow", children: _jsxs(LogoButtonContent, { children: [_jsx(SvgIcon, { name: "streamrTvLogo" }), _jsx(LogoButtonLabel, { children: "StreamrTV" })] }) }) }), broadcasts.length > 0 && (_jsxs(HeaderButtons, { children: [_jsx(CreateLinkPillButton, { type: "button", kind: "secondary", size: "mini", shape: "pill", icon: _jsx(SvgIcon, { name: "link" }), onClick: () => {
                                    scheduleBroadcast();
                                } }), _jsx(CreateLinkButton, { type: "button", kind: "secondary", size: "mini", icon: _jsx(ClockIcon, {}), onClick: () => {
                                    scheduleBroadcast();
                                }, children: "Schedule" }), _jsx(Button, { type: "button", size: "mini", icon: _jsx(OrangePlusIcon, {}), onClick: () => {
                                    startBroadcast({
                                        saveBroadcast: true,
                                    });
                                }, children: "Instant Broadcast" })] }))] }), _jsxs(Content, { children: [_jsx(TopWedge, {}), broadcasts.length === 0 && _jsx(LobbyFrame, {}), pastBroadcasts.length > 0 && (_jsx(BroadcastFrameList, { multi: broadcasts.length > 1, children: pastBroadcasts.map((broadcast) => (_jsx(BroadcastFrame, { broadcast: broadcast, onStartBroadcastClick: ({ streamPartId }) => {
                                startBroadcast({
                                    streamPartId,
                                    saveBroadcast: false,
                                });
                            }, onBroadcastInfoClick: (broadcast) => {
                                scheduleBroadcast(broadcast);
                            } }, broadcast.id))) })), upcomingBroadcasts.length > 0 && (_jsx(BroadcastFrameList, { multi: broadcasts.length > 1, title: "Upcoming", children: upcomingBroadcasts.map((broadcast) => (_jsx(BroadcastFrame, { broadcast: broadcast, onStartBroadcastClick: ({ streamPartId }) => {
                                startBroadcast({
                                    streamPartId,
                                    saveBroadcast: false,
                                });
                            }, onBroadcastInfoClick: (broadcast) => {
                                scheduleBroadcast(broadcast);
                            } }, broadcast.id))) })), _jsx(BottomWedge, {})] }), _jsxs(Footer, { children: [_jsx("div", { children: _jsx(UnstyledLink, { href: "https://streamr.network", target: "_blank", children: _jsxs(PoweredBy, { children: [_jsx("div", { children: "Powered by" }), _jsx(SvgIcon, { name: "streamrLogo" }), _jsx("div", { children: "Streamr" })] }) }) }), _jsx("div", {}), _jsx(Button, { ref: settingsButtonRef, type: "button", kind: "secondary", shape: "pill", padding: "none", variant: "glow", icon: _jsx(SvgIcon, { name: "dotDotDot" }), onClick: async () => {
                            try {
                                await settingsPopup.pop({
                                    anchorRef: settingsButtonRef,
                                });
                            }
                            catch (_snap) { }
                        } })] })] }));
}
const LogoButtonLabel = styled.div ``;
const LogoButtonContent = styled.div `
    align-items: center;
    display: flex;
    gap: 0.375rem;
    padding: 0.375rem;

    ${LogoButtonLabel} {
        display: none;
    }

    @media (min-width: 32.5rem) {
        padding: 0 0.75rem 0 0.375rem;

        ${LogoButtonLabel} {
            display: block;
        }
    }
`;
const LogoButton = styled(Button) `
    display: flex;
    padding: 0;
`;
const CreateLinkPillButton = styled(Button) `
    padding: 0;
`;
const CreateLinkButton = styled(Button) ``;
const HeaderButtons = styled.div `
    align-items: center;
    display: grid;
    gap: 1rem;
    grid-template-columns: auto auto;
    justify-items: end;

    ${CreateLinkButton} {
        display: none;
    }

    @media (min-width: 37.5rem) {
        grid-template-columns: auto auto;

        ${CreateLinkPillButton} {
            display: none;
        }

        ${CreateLinkButton} {
            display: flex;
        }
    }
`;
const Header = styled.div `
    align-items: center;
    backdrop-filter: blur(2rem);
    -webkit-backdrop-filter: blur(2rem);
    box-sizing: border-box;
    display: grid;
    gap: 1rem;
    grid-template-columns: 1fr auto;
    left: 0;
    min-height: 3rem;
    padding: 1.25rem 1rem;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 1;

    @media (min-width: 32.5rem) {
        padding: 2rem;
    }
`;
const TopWedge = styled.div `
    min-height: 7rem;
    height: 25vh;
`;
const BottomWedge = styled.div `
    min-height: 7.75rem;
    height: 25vh;
`;
const Content = styled.div `
    min-width: 0;
    min-height: 100vh;
`;
const UnstyledLink = styled.a `
    color: inherit;
    &:link,
    &:hover,
    &:visited,
    &:active {
        text-decoration: none;
    }
`;
const PoweredBy = styled.div `
    align-items: center;
    color: #ffffff;
    display: flex;
    gap: 0.5rem;
    font-weight: 600;

    > div {
        letter-spacing: -0.03em;
    }
`;
const Footer = styled.div `
    align-items: center;
    backdrop-filter: blur(2rem);
    -webkit-backdrop-filter: blur(2rem);
    bottom: 0;
    box-sizing: border-box;
    display: grid;
    gap: 1rem;
    grid-template-columns: auto 1fr auto;
    left: 0;
    min-height: 3.75rem;
    padding: 1.25rem 1rem;
    position: fixed;
    width: 100%;
    z-index: 1;

    @media (min-width: 32.5rem) {
        align-items: flex-end;
        padding: 2rem;
    }
`;
const HomePageRoot = styled.div `
    height: 100%;
    width: 100%;
    display: grid;
    grid-template-rows: auto 1fr auto;
    overflow: auto;
    color: #ffffff;
`;
